import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { DayPicker } from "react-day-picker";
import { CalendarBlank, CaretLeft, CaretRight } from "@phosphor-icons/react";
import "react-day-picker/dist/style.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseDdMmYyyy = (s) => {
  if (!s) return null;
  const [d, m, y] = s.split(/[/\-]/).map(Number);
  return new Date(y, m - 1, d);
};
const formatDdMmYyyy = (date) => {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
};
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const MAX_FUTURE_DAYS = 4;

// ============== Date pill button ==============

export const CalendarPicker = ({ selectedDate, onSelectDate, displayLabel }) => {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popoverPos, setPopoverPos] = useState(null);
  const triggerRef = useRef(null);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const fourDaysOut = useMemo(() => {
    const t = new Date(today);
    t.setDate(today.getDate() + MAX_FUTURE_DAYS);
    return t;
  }, [today]);

  const dayMap = useMemo(() => {
    const m = {};
    days.forEach((d) => { m[d.date] = d; });
    return m;
  }, [days]);

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/calendar`);
      setDays(data.days || []);
    } catch (e) {
      console.error("Calendar load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Anchor popover to the trigger pill (right-aligned, beneath it)
  const positionPopover = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const popoverWidth = 320;
    const right = window.innerWidth - r.right;
    const top = r.bottom + 8;
    setPopoverPos({ top, right, width: popoverWidth });
  }, []);

  useEffect(() => {
    if (!open) return;
    loadCalendar();
    positionPopover();
    const handler = () => positionPopover();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, loadCalendar, positionPopover]);

  const handlePick = (date) => {
    if (!date) return;
    const ds = formatDdMmYyyy(date);
    const meta = dayMap[ds] || {
      date: ds,
      iso_date: date.toISOString().slice(0, 10),
      label: date.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }),
      selection_count: 0, value_count: 0, settled_count: 0, winners: 0,
      is_today: sameDay(date, today),
      is_past: date < today,
      is_future: date > today,
    };
    onSelectDate(meta);
    setOpen(false);
  };

  const selectedJsDate = selectedDate ? parseDdMmYyyy(selectedDate) : today;

  const modifiers = useMemo(() => {
    const hasSelections = [];
    const hasWinners = [];
    days.forEach((d) => {
      const date = parseDdMmYyyy(d.date);
      if (!date) return;
      if (d.selection_count > 0) hasSelections.push(date);
      if (d.winners > 0) hasWinners.push(date);
    });
    return { hasSelections, hasWinners };
  }, [days]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="calendar-toggle-btn"
        aria-label="Browse other dates"
        title="Browse dates"
        style={{
          background: "#fff",
          border: "1px solid #e5e5e4",
          borderRadius: 999,
          padding: "8px 14px",
          cursor: "pointer",
          color: "#323233",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          fontFamily: "inherit",
          fontWeight: 600,
          letterSpacing: "0.04em",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          transition: "border-color 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#323233"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e5e4"; }}
      >
        <CalendarBlank size={14} weight="bold" />
        <span>{displayLabel || "Pick date"}</span>
      </button>

      {open && popoverPos && createPortal(
        <>
          {/* invisible backdrop to dismiss on outside click */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 2147483599, background: "transparent",
            }}
          />
          <div
            data-testid="calendar-overlay"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: popoverPos.top,
              right: popoverPos.right,
              width: popoverPos.width,
              maxWidth: "calc(100vw - 24px)",
              zIndex: 2147483600,
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 14px 40px rgba(0,0,0,0.14), 0 1px 3px rgba(0,0,0,0.06)",
              border: "1px solid #ececeb",
              padding: "10px 6px 12px",
              animation: "ftpFade 0.18s ease-out",
            }}
          >
            <style>{`
              @keyframes ftpFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
              .ftp-cal .rdp { margin: 0; --rdp-cell-size: 38px; --rdp-accent-color: #323233; --rdp-background-color: #f4f4f3; }
              .ftp-cal .rdp-months { justify-content: center; }
              .ftp-cal .rdp-caption { padding: 0 8px 4px; }
              .ftp-cal .rdp-caption_label { font-size: 14px; font-weight: 700; color: #323233; }
              .ftp-cal .rdp-head_cell { font-size: 10px; font-weight: 600; text-transform: capitalize; color: #999; }
              .ftp-cal .rdp-day { font-size: 13px; color: #323233; border-radius: 10px; position: relative; }
              .ftp-cal .rdp-day:hover:not(.rdp-day_disabled):not(.rdp-day_selected) { background: #f4f4f3; }
              .ftp-cal .rdp-day_disabled { color: #ddd; opacity: 0.6; cursor: not-allowed; }
              .ftp-cal .rdp-day_today { font-weight: 700; }
              .ftp-cal .rdp-day_selected { background: #323233 !important; color: #fff !important; border-radius: 10px; }
              .ftp-cal .rdp-day_outside { color: #ccc; }
              .ftp-cal .rdp-nav_button { color: #555; }
              /* Dot indicators under the day number */
              .ftp-cal .has-selections::after {
                content: ""; position: absolute; bottom: 5px; left: 50%; transform: translateX(-50%);
                width: 4px; height: 4px; border-radius: 2px; background: #323233;
              }
              .ftp-cal .has-winners::after {
                background: #323233 !important;
                width: 5px !important; height: 5px !important;
                border-radius: 2.5px !important;
              }
              .ftp-cal .rdp-day_selected.has-selections::after,
              .ftp-cal .rdp-day_selected.has-winners::after { background: #fff !important; }
            `}</style>
            <div className="ftp-cal">
              <DayPicker
                mode="single"
                selected={selectedJsDate}
                onSelect={handlePick}
                defaultMonth={selectedJsDate || today}
                fromMonth={new Date(today.getFullYear(), today.getMonth() - 6)}
                toDate={fourDaysOut}
                disabled={[{ after: fourDaysOut }]}
                modifiers={modifiers}
                modifiersClassNames={{ hasSelections: "has-selections", hasWinners: "has-winners" }}
                weekStartsOn={1}
              />
            </div>
            {loading && (
              <div style={{ textAlign: "center", color: "#999", fontSize: 10, paddingTop: 4 }}>
                Loading…
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
};

// ============== Viewing-date banner (bordered, theme-coloured) ==============

export const ViewingDateBanner = ({ day, onBackToToday }) => {
  if (!day || day.is_today) return null;
  const Icon = day.is_future ? CaretRight : CaretLeft;
  return (
    <div
      data-testid="viewing-date-banner"
      style={{
        margin: "0 16px 12px",
        padding: "10px 14px",
        background: "#fff",
        border: "1px solid #323233",
        borderRadius: 10,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        fontSize: 12, color: "#323233",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <Icon size={14} weight="bold" />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Viewing <strong>{day.label}</strong>
          {day.is_future && " (early-listed)"}
          {day.is_past && day.settled_count > 0 && (
            <> · {day.winners}/{day.settled_count} winners</>
          )}
        </span>
      </div>
      <button
        type="button"
        onClick={onBackToToday}
        data-testid="back-to-today-btn"
        style={{
          background: "#323233", color: "#fff",
          border: "1px solid #323233",
          padding: "5px 11px", borderRadius: 6, cursor: "pointer",
          fontSize: 11, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.04em",
          flexShrink: 0,
        }}
      >
        Today
      </button>
    </div>
  );
};
